import React, { useState, useEffect, useRef } from "react";
import { Icon } from "@iconify/react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

const MasterLayout = ({ children }) => {
  const [sidebarActive, setSidebarActive] = useState(false);
  const [mobileMenu, setMobileMenu] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false); // State for dropdown
  const dropdownRef = useRef(null);
  
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Enforce light mode globally (night mode disabled)
  useEffect(() => {
    document.documentElement.setAttribute("data-theme", "light");
    localStorage.setItem("theme", "light");
  }, []);

  // Auto-close mobile drawer on route change
  useEffect(() => {
    setMobileMenu(false);
  }, [location.pathname]);

  // Handle clicking outside to close the dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowProfileMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleToggleSidebar = () => {
    if (window.innerWidth < 1200) {
      setMobileMenu((prev) => !prev);
    } else {
      setSidebarActive((prev) => !prev);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/signin");
  };

  const getInitial = () => {
    return user?.name ? user.name.charAt(0).toUpperCase() : "U";
  };

  return (
    <section className={mobileMenu ? "overlay active" : "overlay"}>
      {/* Mobile Drawer Backdrop */}
      {mobileMenu && (
        <div
          className="mobile-overlay-backdrop"
          onClick={() => setMobileMenu(false)}
          aria-hidden="true"
        />
      )}

      {/* ========== SIDEBAR: Restored and Fixed ========== */}
      <aside className={`sidebar ${sidebarActive ? "active" : ""} ${mobileMenu ? "sidebar-open" : ""}`}>
        <button onClick={() => setMobileMenu(false)} type="button" className="sidebar-close-btn" aria-label="Close Sidebar">
          <Icon icon="radix-icons:cross-2" />
        </button>

        <div className="sidebar-header">
          <Link to="/" className="sidebar-logo">
            <img src="/assets/images/logo.png" className="light-logo" alt="QIU | AI" style={{ maxHeight: "42px", width: "auto" }} />
            <img src="/assets/images/logo-light.png" className="dark-logo" alt="QIU | AI" style={{ maxHeight: "42px", width: "auto" }} />
            <img src="/assets/images/logo-icon.png" className="logo-icon" alt="QIU" style={{ maxHeight: "34px", width: "auto" }} />
          </Link>
        </div>

        <div className="sidebar-menu-area">
          <ul className="sidebar-menu">
            <li className="sidebar-menu-group-title">Top Locations</li>
            <li><NavLink to="/locations-top" className={({isActive}) => isActive ? "active-page" : ""}><Icon icon="solar:global-linear" className="menu-icon" /><span>Master Data</span></NavLink></li>
            <li><NavLink to="/locations-google" className={({isActive}) => isActive ? "active-page" : ""}><Icon icon="solar:streets-map-point-linear" className="menu-icon" /><span>Surrounding Places </span></NavLink></li>

            <li className="sidebar-menu-group-title">Main Locations</li>
            <li><NavLink to="/locations-main" className={({isActive}) => isActive ? "active-page" : ""}><Icon icon="solar:city-outline" className="menu-icon" /><span>Master Data</span></NavLink></li>
            <li><NavLink to="/google-business" className={({isActive}) => isActive ? "active-page" : ""}><Icon icon="solar:streets-map-point-linear" className="menu-icon" /><span>Google Places Internal (GBP)</span></NavLink></li>
            <li><NavLink to="/google-places-data" className={({isActive}) => isActive ? "active-page" : ""}><Icon icon="solar:map-point-wave-bold" className="menu-icon" /><span>Google Places External (Places)</span></NavLink></li>

            <li className="sidebar-menu-group-title">Sub Locations</li>
            <li><NavLink to="/locations-sub" className={({isActive}) => isActive ? "active-page" : ""}><Icon icon="solar:shop-outline" className="menu-icon" /><span>Master Data</span></NavLink></li>

            <li className="sidebar-menu-group-title">Client Information</li>
            <li><NavLink to="/" end className={({isActive}) => isActive ? "active-page" : ""}><Icon icon="solar:widget-5-linear" className="menu-icon" /><span>Dashboard</span></NavLink></li>
            <li><NavLink to="/company" className={({isActive}) => isActive ? "active-page" : ""}><Icon icon="solar:buildings-2-linear" className="menu-icon" /><span>Company</span></NavLink></li>
            <li><NavLink to="/google-connections" className={({isActive}) => isActive ? "active-page" : ""}><Icon icon="solar:link-circle-linear" className="menu-icon" /><span>Google Connections</span></NavLink></li>
            <li><NavLink to="/users" className={({isActive}) => isActive ? "active-page" : ""}><Icon icon="solar:user-outline" className="menu-icon" /><span>User Management</span></NavLink></li>

            <li className="sidebar-menu-group-title">Support</li>
            <li><a href="https://support.qiu-one.com" target="_blank" rel="noreferrer"><Icon icon="solar:notes-outline" className="menu-icon" /><span>Ticket System</span></a></li>
          </ul>
        </div>
      </aside>

      {/* ========== MAIN CONTENT AREA ========== */}
      <main className={`dashboard-main ${sidebarActive ? "active" : ""}`}>
        <div className="navbar-header">
          <div className="row align-items-center justify-content-between">
            <div className="col-auto d-flex align-items-center">
              {/* Desktop toggle button */}
              <button 
                type="button" 
                className="sidebar-toggle" 
                onClick={() => setSidebarActive(!sidebarActive)}
                aria-label="Toggle Desktop Sidebar"
              >
                <Icon icon={sidebarActive ? "iconoir:arrow-right" : "heroicons:bars-3-solid"} className="icon text-xl" />
              </button>

              {/* Mobile burger menu button */}
              <button 
                type="button" 
                className="sidebar-mobile-toggle" 
                onClick={() => setMobileMenu(!mobileMenu)}
                aria-label="Toggle Mobile Menu"
              >
                <Icon icon="heroicons:bars-3-solid" className="icon text-xl" />
              </button>
            </div>

            <div className="col-auto">
              <div className="d-flex align-items-center gap-3">
                {/* PROFILE DROPDOWN: Fixed Toggle logic */}
                <div className="dropdown" ref={dropdownRef}>
                  <button 
                    className="border-0 bg-transparent p-0 d-flex align-items-center"
                    type="button"
                    onClick={() => setShowProfileMenu(!showProfileMenu)}
                  >
                    <div className="qiu-avatar-btn w-36-px h-36-px rounded-circle d-flex align-items-center justify-content-center fw-semibold text-sm">
                      {getInitial()}
                    </div>
                  </button>

                  <ul 
                    className={`dropdown-menu to-top qiu-dropdown-menu radius-12 py-0 overflow-hidden ${showProfileMenu ? 'show' : ''}`}
                    style={{ 
                        minWidth: '240px', 
                        display: showProfileMenu ? 'block' : 'none',
                        position: 'absolute',
                        right: 0,
                        top: '46px',
                        zIndex: 1000
                    }}
                  >
                    <div className="py-14 px-16 qiu-dropdown-header border-bottom">
                      <h6 className="text-sm mb-0 text-primary-light fw-semibold">{user?.name || "User Account"}</h6>
                      <span className="text-xs text-secondary-light">{user?.email || "Administrator"}</span>
                    </div>
                    
                    <div className="p-8">
                      <li>
                        <Link to="/profile" className="dropdown-item radius-8 text-black px-12 py-8 hover-bg-primary-50 d-flex align-items-center gap-2 border-0 bg-transparent transition-all text-xs fw-medium" onClick={() => setShowProfileMenu(false)}>
                          <Icon icon="solar:user-circle-outline" className="icon text-lg text-secondary-light" /> 
                          <span>My Profile</span>
                        </Link>
                      </li>
                      <li>
                        <button className="dropdown-item radius-8 text-danger-main px-12 py-8 hover-bg-danger-focus d-flex align-items-center gap-2 border-0 bg-transparent w-100 transition-all text-xs fw-medium" onClick={handleLogout}>
                          <Icon icon="lucide:power" className="icon text-base text-danger-main" /> 
                          <span>Sign Out</span>
                        </button>
                      </li>
                    </div>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="dashboard-main-body">
            {children}
        </div>
      </main>
    </section>
  );
};

export default MasterLayout;